import React, { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../services/api";
import type { AuthUser } from "../hooks/useAuth";
import { useVesselWorkTypeOptions } from "../hooks/useVesselWorkTypeOptions";
import { BottomNav } from "../components/BottomNav";
import { BackButton } from "../components/BackButton";
import { isAdminMonitor } from "../utils/adminMonitor";
import { judgeAlcohol } from "../utils/alcoholThresholds";

interface Props { user: AuthUser; }

function getPermissions(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem("catms_permissions") ?? "{}"); }
  catch { return {}; }
}

// ── 型定義 ────────────────────────────────────────────────────────────
interface CrewMember {
  user_id: string; user_name: string; employee_code: string;
  avatar_path: string | null; position: string | null;
  status: "working" | "idle";
  started_at: string | null; work_type: string | null;
  elapsed_minutes: number | null; total_work_minutes: number;
  // 本日(JST)の打刻とアルコール測定（管理者の確認専用モードで表示）
  today_clock_in?: string | null; today_clock_out?: string | null; today_alcohol?: number | null;
}
interface AttendanceRecord {
  id: string; work_date: string; clock_in: string; clock_out: string | null;
  work_minutes: number; over_minutes: number; break_minutes: number;
  status: string; work_type: string | null; vessel_id?: string;
}

interface ActivityMaster { id: string; name: string; emoji: string; color: string; }
interface ActivityLog { id: string; activity_master_id: string; name: string; emoji: string; color: string; started_at: string; ended_at: string; duration_minutes: number; note?: string; }

function CrewActivityLogModal({ sessionId, vesselId, sessionDate, onClose }: {
  sessionId: string; vesselId: string; sessionDate: string;
  onClose: () => void; onSaved: () => void;
}) {
  const [activityTypes, setActivityTypes] = React.useState<ActivityMaster[]>([]);
  const [existingLogs, setExistingLogs] = React.useState<ActivityLog[]>([]);
  const [selected, setSelected] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [deleteConfirm, setDeleteConfirm] = React.useState<string | null>(null);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [typesRes, logsRes] = await Promise.all([
          apiFetch<any>(`/vessels/${vesselId}/activity-types`),
          apiFetch<any>(`/activity-logs?work_session_id=${sessionId}`),
        ]);
        setActivityTypes(typesRes?.data ?? []);
        setExistingLogs(logsRes?.data ?? []);
      } catch { /* noop */ }
      finally { setLoading(false); }
    };
    if (vesselId) load();
  }, [sessionId, vesselId]);

  const handleSave = async () => {
    if (!selected) { setError('作業種別を選択してください'); return; }
    if (!startTime || !endTime) { setError('開始・終了時刻を入力してください'); return; }
    const startIso = `${sessionDate}T${startTime}:00+09:00`;
    const endIso   = `${sessionDate}T${endTime}:00+09:00`;
    if (new Date(endIso) <= new Date(startIso)) { setError('終了時刻は開始時刻より後にしてください'); return; }
    setSaving(true); setError('');
    try {
      await apiFetch<any>('/activity-logs', {
        method: 'POST',
        body: JSON.stringify({ work_session_id: sessionId, vessel_id: vesselId, activity_master_id: selected, started_at: startIso, ended_at: endIso, note: note || undefined }),
      });
      const res = await apiFetch<any>(`/activity-logs?work_session_id=${sessionId}`);
      setExistingLogs(res?.data ?? []);
      setSelected(''); setStartTime(''); setEndTime(''); setNote('');
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiFetch<any>(`/activity-logs/${id}`, { method: 'DELETE' });
      setExistingLogs(p => p.filter(l => l.id !== id));
    } catch { /* noop */ }
    setDeleteConfirm(null);
  };

  const fmtIso = (iso: string) => {
    try { return new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo', hour12: false }); } catch { return iso; }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 -8px 32px rgba(0,0,0,0.15)', maxHeight: '85dvh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>臨時作業申請</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{sessionDate}</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: 'none', background: '#f1f5f9', borderRadius: '50%', cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>読み込み中...</div>
        ) : !vesselId ? (
          <div style={{ background: '#fef2f2', borderRadius: 10, padding: 14, color: '#dc2626', fontSize: 13 }}>船舶情報が取得できませんでした。</div>
        ) : (
          <>
            {existingLogs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>登録済み</div>
                {existingLogs.map(l => (
                  <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderRadius: 8, padding: '8px 10px', marginBottom: 6 }}>
                    {deleteConfirm === l.id ? (
                      <>
                        <span style={{ fontSize: 12, color: '#dc2626' }}>削除しますか？</span>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => setDeleteConfirm(null)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}>キャンセル</button>
                          <button onClick={() => handleDelete(l.id)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>削除</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <span style={{ fontSize: 13, fontWeight: 600, marginRight: 8 }}>{l.emoji} {l.name}</span>
                          <span style={{ fontSize: 11, color: '#64748b' }}>{fmtIso(l.started_at)} – {fmtIso(l.ended_at)} ({l.duration_minutes}分)</span>
                        </div>
                        <button onClick={() => setDeleteConfirm(l.id)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>削除</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>新規追加</div>
            {activityTypes.length === 0 ? (
              <div style={{ background: '#fef9c3', borderRadius: 10, padding: 12, color: '#854d0e', fontSize: 13 }}>この船舶には特別作業種別が登録されていません。</div>
            ) : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  {activityTypes.map(t => (
                    <button key={t.id} onClick={() => setSelected(t.id)}
                      style={{ padding: '8px 14px', borderRadius: 20, border: `2px solid ${selected === t.id ? t.color : '#e2e8f0'}`, background: selected === t.id ? t.color + '20' : '#fff', cursor: 'pointer', fontSize: 13, fontWeight: selected === t.id ? 700 : 400 }}>
                      {t.emoji} {t.name}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>開始時刻</span>
                    <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 14 }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>終了時刻</span>
                    <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 14 }} />
                  </label>
                </div>
                <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="メモ（任意）"
                  style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 13, resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 12 }} />
                {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={onClose} style={{ flex: 1, padding: 12, border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>閉じる</button>
                  <button onClick={handleSave} disabled={saving || !selected} style={{ flex: 2, padding: 12, border: 'none', borderRadius: 10, background: saving || !selected ? '#c4b5fd' : '#7c3aed', color: '#fff', fontWeight: 700, cursor: saving || !selected ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                    {saving ? '保存中...' : '申請する'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── ユーティリティ ────────────────────────────────────────────────────
const WEEKDAYS = ["日","月","火","水","木","金","土"];
function fmtElapsed(min: number | null): string {
  if (!min || min < 0) return "";
  const h = Math.floor(min / 60), m = min % 60;
  return h > 0 ? `${h}h${m}m経過` : `${m}m経過`;
}
function fmtHours(min: number): string {
  if (!min) return "0h";
  const h = Math.floor(min / 60), m = min % 60;
  return m > 0 ? `${h}h${m}m` : `${h}h`;
}
function toJstHHMM(iso: string): string {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tokyo", hour12: false }); }
  catch { return ""; }
}
function initials(name: string): string { return name.replace(/\s/g, "").slice(0, 2); }
// recorded_at はタイムゾーン無しUTC（'YYYY-MM-DD HH:MM:SS'）の場合がある。UTCとして解釈してJST表示
function utcToJstHHMM(ts: string | null): string {
  if (!ts) return "";
  try {
    const hasZone = /[zZ]$|[+-]\d\d:?\d\d$/.test(ts);
    const d = new Date(ts.replace(" ", "T") + (hasZone ? "" : "Z"));
    return d.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tokyo", hour12: false });
  } catch { return ""; }
}

// ── 勤怠追加/編集モーダル ──────────────────────────────────────────────
function addOneDayCrew(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function AttendanceModal({ title, date, record, onSave, onClose }: {
  title: string; date: string;
  record?: AttendanceRecord;
  onSave: (data: { clock_in: string; clock_out?: string; clock_out_date?: string; break_minutes: number; reason: string; work_type?: string }) => Promise<void>;
  onClose: () => void;
}) {
  const WTYPE_OPTS = useVesselWorkTypeOptions(record?.vessel_id);
  const [clockIn, setClockIn]   = useState(record ? toJstHHMM(record.clock_in) : "09:00");
  const [clockOut, setClockOut] = useState(record?.clock_out ? toJstHHMM(record.clock_out) : "");
  const [breakMin, setBreakMin] = useState(String(record?.break_minutes ?? 60));
  const [workType, setWorkType] = useState(record?.work_type ?? "");
  const [reason, setReason]     = useState("");
  const [error, setError]       = useState("");
  const [saving, setSaving]     = useState(false);
  const [clockOutNextDay, setClockOutNextDay] = useState(false);

  const nextDate = addOneDayCrew(date);

  const handleSave = async () => {
    if (!reason.trim()) { setError("理由を入力してください"); return; }
    if (!clockIn) { setError("開始時刻を入力してください"); return; }
    setSaving(true); setError("");
    try {
      await onSave({
        clock_in: clockIn, clock_out: clockOut || undefined,
        clock_out_date: clockOutNextDay ? nextDate : undefined,
        break_minutes: Number(breakMin), reason, work_type: workType || undefined,
      });
      onClose();
    } catch (e: any) { setError(e.message ?? "保存に失敗しました"); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", zIndex: 1000 }}>
      <div style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: 24, width: "100%", maxWidth: 480, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{title}</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{date}</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: "none", background: "#f1f5f9", borderRadius: "50%", cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>開始時刻</span>
            <input type="time" value={clockIn} onChange={e => setClockIn(e.target.value)}
              style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", fontSize: 15, outline: "none", background: "#f8fafc" }} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>終了時刻</span>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <input type="time" value={clockOut} onChange={e => setClockOut(e.target.value)}
                style={{ flex: 1, border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", fontSize: 15, outline: "none", background: "#f8fafc" }} />
              <button type="button" onClick={() => setClockOutNextDay(p => !p)}
                style={{ padding: "8px 7px", fontSize: 10, fontWeight: 700, borderRadius: 8, border: "none", cursor: "pointer", background: clockOutNextDay ? "#7c3aed" : "#f1f5f9", color: clockOutNextDay ? "#fff" : "#64748b", whiteSpace: "nowrap" }}>
                +1日
              </button>
            </div>
            {clockOutNextDay && <span style={{ fontSize: 10, color: "#7c3aed", fontWeight: 600 }}>翌日 {nextDate}</span>}
          </label>
        </div>
        <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>休憩（分）</span>
          <input type="number" value={breakMin} min={0} onChange={e => setBreakMin(e.target.value)}
            style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", fontSize: 15, outline: "none", background: "#f8fafc" }} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>作業種別</span>
          <select value={workType} onChange={e => setWorkType(e.target.value)}
            style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", fontSize: 14, outline: "none", background: "#f8fafc", color: "#0f172a" }}>
            <option value="">（未設定）</option>
            {workType && !WTYPE_OPTS.includes(workType) && <option value={workType}>{workType}</option>}
            {WTYPE_OPTS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#0057d9" }}>理由（必須）</span>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="例：打刻漏れのため"
            style={{ border: "1px solid #bfdbfe", borderRadius: 10, padding: "10px 12px", fontSize: 13, resize: "none", fontFamily: "inherit", background: "#eff6ff", outline: "none" }} />
        </label>
        {error && <div style={{ color: "#dc2626", fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 14, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", cursor: "pointer", fontSize: 14, color: "#475569", fontWeight: 600 }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: 14, border: "none", borderRadius: 12, background: saving ? "#93c5fd" : "#0057d9", color: "#fff", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", fontSize: 14 }}>
            {saving ? "保存中..." : "保存する"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CrewActivityDatePickerModal({ crew, onClose, onSubmit }: {
  crew: CrewMember;
  onClose: () => void;
  onSubmit: (date: string) => void;
}) {
  const [date, setDate] = React.useState(new Date().toISOString().slice(0, 10));
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", zIndex: 1000 }}>
      <div style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: 24, width: "100%", maxWidth: 480, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>特別作業を追加</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{crew.user_name}</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: "none", background: "#f1f5f9", borderRadius: "50%", cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>
        <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>作業日</span>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", fontSize: 15, outline: "none", background: "#f8fafc" }} />
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 14, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", cursor: "pointer", fontSize: 14, color: "#475569", fontWeight: 600 }}>キャンセル</button>
          <button onClick={() => onSubmit(date)} style={{ flex: 2, padding: 14, border: "none", borderRadius: 12, background: "#7c3aed", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>続ける</button>
        </div>
      </div>
    </div>
  );
}

// ── メインページ ──────────────────────────────────────────────────────
const CrewStatusPage: React.FC<Props> = ({ user }) => {
  // 権限は position_permissions から取得（user_role ではなく vessel 配置役職ベース）
  const perms = getPermissions();
  const canView = perms.view_crew_attendance === true ||
    ["system_admin", "shore_admin"].includes(user.user_role);
  const isCaptain = perms.edit_crew_attendance === true ||
    ["system_admin", "shore_admin"].includes(user.user_role);
  const [tab, setTab] = useState<"status" | "manage">("status");

  // 状況タブ
  const [statusData, setStatusData] = useState<{ vessel_name: string; crew: CrewMember[] } | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState("");

  // 管理者の確認専用モード: 船を切り替えて各船の打刻状況を見る
  const adminMon = isAdminMonitor();
  const [vessels, setVessels] = useState<{ id: string; vessel_name: string; crew_count?: number }[]>([]);
  const [selVessel, setSelVessel] = useState("");
  useEffect(() => {
    if (!adminMon) return;
    apiFetch<{ data: { id: string; vessel_name: string; crew_count?: number }[] }>("/vessels")
      .then(r => {
        const vs = r?.data ?? [];
        setVessels(vs);
        setSelVessel(prev => prev || (vs[0]?.id ?? ""));
      })
      .catch(() => setVessels([]));
  }, [adminMon]);

  // 勤怠管理タブ
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [expanded, setExpanded]   = useState<string | null>(null);
  const [attendance, setAttendance] = useState<Record<string, AttendanceRecord[]>>({});
  const [loadingAtt, setLoadingAtt] = useState<string | null>(null);
  const [editModal, setEditModal]   = useState<{ crew: CrewMember; record?: AttendanceRecord; date?: string } | null>(null);
  const [editModalForActivity, setEditModalForActivity] = useState(false);
  const [crewActivityPicker, setCrewActivityPicker] = useState<{ crew: CrewMember } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; crewName: string; date: string } | null>(null);
  const [activityModal, setActivityModal] = useState<{ sessionId: string; vesselId: string; date: string } | null>(null);

  const fetchStatus = useCallback(async () => {
    // 確認専用モードの管理者は自分の配乗が無いので、選んだ船で取得する（船リスト取得待ちの間は何もしない）
    if (adminMon && !selVessel) return;
    try {
      const res = await apiFetch<{ success: boolean; data: { vessel_name: string; crew: CrewMember[] } }>(
        adminMon ? `/crew-status?vessel_id=${selVessel}` : "/crew-status");
      setStatusData(res.data); setStatusError("");
    } catch (e: any) {
      setStatusError(e.message?.includes("権限") ? "乗組員状況の閲覧には権限が必要です。" : "取得に失敗しました。");
    } finally { setStatusLoading(false); }
  }, [adminMon, selVessel]);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 30_000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  async function loadAttendance(userId: string) {
    setLoadingAtt(userId);
    try {
      const res = await apiFetch<any>(`/attendance?user_id=${userId}&year=${year}&month=${String(month).padStart(2,"0")}`);
      // 管理者の操作（船員デモ・配乗の無い管理者の打刻）は勤怠ではないので出さない
      const records: AttendanceRecord[] = (res?.data ?? []).filter((r: any) => Number(r.admin_operated ?? 0) !== 1).map((r: any) => ({
        id: r.id, work_date: r.work_date ?? r.target_date ?? "",
        clock_in: r.clock_in, clock_out: r.clock_out,
        work_minutes: r.work_minutes ?? 0, over_minutes: r.over_minutes ?? 0,
        break_minutes: r.break_minutes ?? 0, status: r.status ?? "completed",
        work_type: r.work_type ?? null, vessel_id: r.vessel_id ?? undefined,
      }));
      setAttendance(prev => ({ ...prev, [userId]: records }));
    } catch { /* ignore */ }
    finally { setLoadingAtt(null); }
  }

  function toggleExpand(userId: string) {
    if (expanded === userId) { setExpanded(null); return; }
    setExpanded(userId);
    if (!attendance[userId]) loadAttendance(userId);
  }

  useEffect(() => {
    if (tab === "manage" && expanded) loadAttendance(expanded);
  }, [year, month]);

  async function handleSaveAttendance(crew: CrewMember, record: AttendanceRecord | undefined, date: string | undefined, data: { clock_in: string; clock_out?: string; clock_out_date?: string; break_minutes: number; reason: string; work_type?: string }): Promise<{ id: string; vessel_id: string } | undefined> {
    const work_date = record?.work_date ?? date ?? "";
    if (record) {
      await apiFetch<any>(`/attendance/${record.id}`, {
        method: "PUT",
        body: JSON.stringify({ user_id: crew.user_id, clock_in: data.clock_in, clock_out: data.clock_out, clock_out_date: data.clock_out_date, break_minutes: data.break_minutes, work_type: data.work_type ?? null, reason: data.reason }),
      });
      loadAttendance(crew.user_id);
      return undefined;
    } else {
      const res = await apiFetch<{ data: { id: string; vessel_id: string; work_date: string } }>("/attendance", {
        method: "POST",
        body: JSON.stringify({ user_id: crew.user_id, work_date, clock_in: data.clock_in, clock_out: data.clock_out, clock_out_date: data.clock_out_date, break_minutes: data.break_minutes, work_type: data.work_type ?? null, reason: data.reason }),
      });
      loadAttendance(crew.user_id);
      return res?.data ? { id: String(res.data.id), vessel_id: res.data.vessel_id ?? '' } : undefined;
    }
  }

  function handleActivityForCrew(crew: CrewMember, date: string) {
    const userAtt = attendance[crew.user_id] ?? [];
    const session = userAtt.find(r => (r.work_date ?? '').slice(0, 10) === date.slice(0, 10));
    setCrewActivityPicker(null);
    if (session) {
      setActivityModal({ sessionId: session.id, vesselId: session.vessel_id ?? '', date: session.work_date });
    } else {
      setEditModal({ crew, date });
      setEditModalForActivity(true);
    }
  }

  async function handleDelete(id: string, userId: string) {
    try { await apiFetch<any>(`/attendance/${id}`, { method: "DELETE" }); } catch { /* ignore */ }
    setDeleteConfirm(null);
    loadAttendance(userId);
  }

  const working = statusData?.crew.filter(c => c.status === "working") ?? [];
  const idle    = statusData?.crew.filter(c => c.status === "idle") ?? [];
  const allCrew = statusData?.crew ?? [];

  // 閲覧権限なしの場合はブロック画面
  if (!canView) {
    return (
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, background: "#f8fafc" }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center" }}>
          <div style={{ width: 64, height: 64, background: "#fef2f2", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>閲覧権限がありません</div>
          <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.6 }}>
            「他船員の勤怠閲覧」権限が設定されていません。<br />
            管理者に役職権限設定の確認を依頼してください。
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, background: "#f8fafc", fontFamily: "'Inter','Hiragino Sans',sans-serif" }}>

      {/* ヘッダー */}
      <div className="app-header" style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "0 16px 0", flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <BackButton />
              <div style={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>乗組員</div>
            </div>
            {statusData?.vessel_name && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>🚢 {statusData.vessel_name}</div>}
          </div>
          {tab === "status" && (
            <button onClick={() => { setStatusLoading(true); fetchStatus(); }} style={{ fontSize: 12, padding: "6px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#64748b", cursor: "pointer" }}>
              更新
            </button>
          )}
          {tab === "manage" && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#f8fafc", borderRadius: 10, padding: "5px 8px", border: "1px solid #e2e8f0" }}>
              <button onClick={() => { if (month===1){setYear(y=>y-1);setMonth(12);}else setMonth(m=>m-1); }} style={{ width: 24, height: 24, border: "none", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 14, color: "#374151", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>‹</button>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", minWidth: 60, textAlign: "center" }}>{year}年{month}月</span>
              <button onClick={() => { if (month===12){setYear(y=>y+1);setMonth(1);}else setMonth(m=>m+1); }} style={{ width: 24, height: 24, border: "none", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 14, color: "#374151", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>›</button>
            </div>
          )}
        </div>

        {/* 管理者の確認専用モード: 船の切り替え */}
        {adminMon && vessels.length > 0 && (
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch" }}>
            {vessels.map(v => (
              <button key={v.id}
                onClick={() => { if (v.id !== selVessel) { setStatusLoading(true); setSelVessel(v.id); setExpanded(null); } }}
                style={{ flexShrink: 0, padding: "7px 14px", borderRadius: 999,
                  border: "1px solid " + (selVessel === v.id ? "#0057d9" : "#e2e8f0"),
                  background: selVessel === v.id ? "#eff6ff" : "#fff",
                  color: selVessel === v.id ? "#0057d9" : "#64748b",
                  fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                🚢 {v.vessel_name}{typeof v.crew_count === "number" ? `（${v.crew_count}名）` : ""}
              </button>
            ))}
          </div>
        )}

        {/* タブ */}
        <div style={{ display: "flex", gap: 0 }}>
          {[{ key: "status", label: "現在の状況" }, { key: "manage", label: "勤怠管理" }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key as "status"|"manage")}
              style={{ flex: 1, padding: "10px 0", fontSize: 13, fontWeight: tab === t.key ? 700 : 500, border: "none", background: "none", cursor: "pointer",
                color: tab === t.key ? "#0057d9" : "#64748b",
                borderBottom: tab === t.key ? "2px solid #0057d9" : "2px solid transparent" }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─── 状況タブ ─────────────────────────────────── */}
      {tab === "status" && (
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
          {statusLoading ? (
            <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>読み込み中...</div>
          ) : statusError ? (
            <div style={{ margin: "40px 16px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12, padding: 20, textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🔒</div>
              <div style={{ fontSize: 13, color: "#92400e", lineHeight: 1.6 }}>{statusError}</div>
            </div>
          ) : allCrew.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
              <div style={{ fontSize: 14 }}>乗組員が配置されていません</div>
            </div>
          ) : (
            <>
              {/* 統計 */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[
                  { label: "業務中", value: working.length, color: "#059669", bg: "#f0fdf4" },
                  { label: "待機中", value: idle.length,    color: "#94a3b8", bg: "#f8fafc" },
                ].map(s => (
                  <div key={s.label} style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #e5e7eb" }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* 業務中 */}
              {working.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#059669", marginBottom: 6, paddingLeft: 2 }}>● 業務中</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {working.map(c => <StatusCard key={c.user_id} crew={c} showToday={adminMon} />)}
                  </div>
                </div>
              )}

              {/* 待機中 */}
              {idle.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", marginBottom: 6, paddingLeft: 2 }}>○ 待機中</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {idle.map(c => <StatusCard key={c.user_id} crew={c} showToday={adminMon} />)}
                  </div>
                </div>
              )}
              <div style={{ textAlign: "center", fontSize: 11, color: "#cbd5e1", marginTop: 16 }}>30秒ごとに自動更新</div>
            </>
          )}
        </div>
      )}

      {/* ─── 勤怠管理タブ ────────────────────────────── */}
      {tab === "manage" && (
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
          {allCrew.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>乗組員がいません</div>
          ) : allCrew.map(crew => {
            const isOpen = expanded === crew.user_id;
            const records = attendance[crew.user_id] ?? [];
            const totalWork = records.reduce((s, r) => s + r.work_minutes, 0);
            const workedDays = records.length;
            return (
              <div key={crew.user_id} style={{ background: "#fff", borderRadius: 14, marginBottom: 10, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                {/* 乗組員ヘッダー行 */}
                <div onClick={() => toggleExpand(crew.user_id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: "pointer" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: crew.status === "working" ? "linear-gradient(135deg,#10b981,#059669)" : "linear-gradient(135deg,#94a3b8,#64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
                    {initials(crew.user_name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{crew.user_name}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                      {workedDays > 0 ? `${workedDays}日 / ${fmtHours(totalWork)}` : "記録なし"}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: crew.status === "working" ? "#f0fdf4" : "#f8fafc", color: crew.status === "working" ? "#059669" : "#94a3b8" }}>
                      {crew.status === "working" ? "業務中" : "待機中"}
                    </span>
                    <span style={{ fontSize: 16, color: "#94a3b8", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>›</span>
                  </div>
                </div>

                {/* 展開: 勤怠一覧 */}
                {isOpen && (
                  <div style={{ borderTop: "1px solid #f1f5f9", background: "#fafafa", padding: "10px 14px" }}>
                    {loadingAtt === crew.user_id ? (
                      <div style={{ textAlign: "center", padding: 20, color: "#94a3b8", fontSize: 13 }}>読み込み中...</div>
                    ) : (
                      <>
                        {records.length === 0 ? (
                          <div style={{ textAlign: "center", padding: "14px 0", color: "#94a3b8", fontSize: 13 }}>この月の記録はありません</div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {records.map(r => {
                              const dow = WEEKDAYS[new Date(r.work_date).getDay()] ?? "";
                              const isSun = dow === "日", isSat = dow === "土";
                              const isActive = r.status === "working";
                              return (
                                <div key={r.id} style={{ background: "#fff", borderRadius: 10, padding: "10px 12px", border: `1px solid ${isActive ? "#fde68a" : "#f1f5f9"}` }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div>
                                      {/* 日付 + 時刻 */}
                                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: isSun ? "#ef4444" : isSat ? "#3b82f6" : "#374151" }}>
                                          {r.work_date.slice(5)} ({dow})
                                        </span>
                                        <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{toJstHHMM(r.clock_in)}</span>
                                        <span style={{ fontSize: 12, color: "#cbd5e1" }}>→</span>
                                        {isActive
                                          ? <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", background: "#fffbeb", padding: "2px 6px", borderRadius: 99, border: "1px solid #fde68a" }}>勤務中</span>
                                          : <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{r.clock_out ? toJstHHMM(r.clock_out) : "—"}</span>
                                        }
                                      </div>
                                      {/* バッジ */}
                                      <div style={{ display: "flex", gap: 5 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: "#0057d9", background: "#eff6ff", padding: "2px 7px", borderRadius: 99 }}>{fmtHours(r.work_minutes)}</span>
                                        {r.over_minutes > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: "#dc2626", background: "#fef2f2", padding: "2px 7px", borderRadius: 99 }}>残業 {fmtHours(r.over_minutes)}</span>}
                                        {r.work_type && <span style={{ fontSize: 11, color: "#64748b", background: "#f8fafc", padding: "2px 7px", borderRadius: 99 }}>{r.work_type}</span>}
                                      </div>
                                    </div>
                                    {/* 船長・管理者のみ編集ボタン表示 */}
                                    {isCaptain && (
                                      <div style={{ display: "flex", gap: 4, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                        <button onClick={() => setActivityModal({ sessionId: r.id, vesselId: r.vessel_id ?? '', date: r.work_date })}
                                          style={{ fontSize: 11, padding: "5px 10px", border: "1px solid #c4b5fd", borderRadius: 8, background: "#f5f3ff", color: "#7c3aed", cursor: "pointer", fontWeight: 600 }}>
                                          特別作業
                                        </button>
                                        <button onClick={() => setEditModal({ crew, record: r })}
                                          style={{ fontSize: 11, padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#374151", cursor: "pointer", fontWeight: 600 }}>
                                          修正
                                        </button>
                                        <button onClick={() => setDeleteConfirm({ id: r.id, crewName: crew.user_name, date: r.work_date })}
                                          style={{ fontSize: 11, padding: "5px 10px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#dc2626", cursor: "pointer", fontWeight: 600 }}>
                                          削除
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* 追加ボタン（船長のみ） */}
                        {isCaptain && (
                          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                            <button onClick={() => setEditModal({ crew, date: new Date().toISOString().slice(0, 10) })}
                              style={{ flex: 1, padding: "10px", border: "1.5px dashed #bfdbfe", borderRadius: 10, background: "#f8fafc", color: "#0057d9", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                              ＋ 勤怠を追加
                            </button>
                            <button onClick={() => { if (!attendance[crew.user_id]) loadAttendance(crew.user_id); setCrewActivityPicker({ crew }); }}
                              style={{ flex: 1, padding: "10px", border: "1.5px dashed #c4b5fd", borderRadius: 10, background: "#faf5ff", color: "#7c3aed", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                              ＋ 特別作業
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <BottomNav />

      {/* 追加/編集モーダル */}
      {editModal && (
        <AttendanceModal
          title={editModal.record ? `${editModal.crew.user_name}の勤怠を修正` : `${editModal.crew.user_name}の勤怠を追加`}
          date={editModal.record?.work_date ?? editModal.date ?? ""}
          record={editModal.record}
          onSave={async data => {
            const result = await handleSaveAttendance(editModal.crew, editModal.record, editModal.date, data);
            if (editModalForActivity && result?.id) {
              setEditModalForActivity(false);
              setActivityModal({ sessionId: result.id, vesselId: result.vessel_id ?? '', date: editModal.date ?? '' });
            }
          }}
          onClose={() => { setEditModal(null); setEditModalForActivity(false); }}
        />
      )}

      {/* 特別作業日付選択 */}
      {crewActivityPicker && (
        <CrewActivityDatePickerModal
          crew={crewActivityPicker.crew}
          onClose={() => setCrewActivityPicker(null)}
          onSubmit={date => handleActivityForCrew(crewActivityPicker.crew, date)}
        />
      )}

      {/* 特別作業モーダル */}
      {activityModal && (
        <CrewActivityLogModal
          sessionId={activityModal.sessionId}
          vesselId={activityModal.vesselId}
          sessionDate={activityModal.date}
          onClose={() => setActivityModal(null)}
          onSaved={() => { setActivityModal(null); if (expanded) loadAttendance(expanded); }}
        />
      )}

      {/* 削除確認 */}
      {deleteConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: 24, width: "100%", maxWidth: 480, margin: "0 auto" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>勤怠を削除</div>
            <div style={{ background: "#fef2f2", border: "1px solid #fecdd3", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#dc2626" }}>
              {deleteConfirm.crewName} — {deleteConfirm.date} の記録を削除します
            </div>
            <p style={{ margin: "0 0 20px", fontSize: 12, color: "#94a3b8" }}>この操作は取り消せません。</p>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: 14, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", cursor: "pointer", fontSize: 14, color: "#475569", fontWeight: 600 }}>キャンセル</button>
              <button onClick={() => handleDelete(deleteConfirm.id, allCrew.find(c => c.user_name === deleteConfirm.crewName)?.user_id ?? "")}
                style={{ flex: 2, padding: 14, border: "none", borderRadius: 12, background: "#dc2626", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>削除する</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ── 状況カード ────────────────────────────────────────────────────────
// showToday: 管理者の確認専用モードで「本日の出勤・退勤・アルコール測定結果」を1行添える
const StatusCard: React.FC<{ crew: CrewMember; showToday?: boolean }> = ({ crew: c, showToday }) => {
  const isWorking = c.status === "working";
  const alcoholBadge = () => {
    if (c.today_alcohol == null) {
      return <span style={{ background: "#f8fafc", color: "#94a3b8", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>🧪 測定なし</span>;
    }
    const j = judgeAlcohol(c.today_alcohol);
    const sty = j === "critical" ? { background: "#fee2e2", color: "#dc2626" }
              : j === "warning"  ? { background: "#fffbeb", color: "#d97706" }
              :                    { background: "#f0fdf4", color: "#16a34a" };
    return (
      <span style={{ ...sty, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>
        🧪 {Number(c.today_alcohol).toFixed(3)}{j === "critical" ? " 基準超過" : j === "warning" ? " 要確認" : ""}
      </span>
    );
  };
  return (
    <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 44, height: 44, borderRadius: "50%", flexShrink: 0, background: isWorking ? "linear-gradient(135deg,#10b981,#059669)" : "linear-gradient(135deg,#94a3b8,#64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: "#fff" }}>
        {initials(c.user_name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{c.user_name}</span>
          {c.position && <span style={{ background: "#ede9fe", color: "#7c3aed", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>{c.position}</span>}
        </div>
        {isWorking ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {c.work_type && <span style={{ background: "#f0fdf4", color: "#16a34a", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{c.work_type}</span>}
            <span style={{ fontSize: 12, color: "#10b981", fontWeight: 600 }}>{c.elapsed_minutes ? fmtElapsed(c.elapsed_minutes) : "業務中"}</span>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>今月 {fmtHours(c.total_work_minutes)}</span>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "#94a3b8" }}>待機中　今月 {fmtHours(c.total_work_minutes)}</div>
        )}
        {showToday && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            {c.today_clock_in ? (
              <>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>本日 {utcToJstHHMM(c.today_clock_in)}</span>
                <span style={{ fontSize: 11, color: "#cbd5e1" }}>→</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: c.today_clock_out ? "#0f172a" : "#d97706" }}>
                  {c.today_clock_out ? utcToJstHHMM(c.today_clock_out) : "勤務中"}
                </span>
                {alcoholBadge()}
              </>
            ) : (
              <span style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>本日の打刻なし</span>
            )}
          </div>
        )}
      </div>
      <div style={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, background: isWorking ? "#10b981" : "#d1d5db", boxShadow: isWorking ? "0 0 0 3px rgba(16,185,129,0.2)" : "none" }} />
    </div>
  );
};

export default CrewStatusPage;
